/**
 * Contact form.
 *
 * The field set follows the project specification in README.md, NOT the current Figma
 * mock-up: the mock-up is missing the phone field and the captcha, and splits the
 * contact person into first name / last name plus address fields. See
 * docs/section-inventory.md, section 38. When the reworked mock-up arrives, only the
 * markup here changes - the payload and the Strapi Submission model already match.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Blocks, Heading, Lead, SectionShell } from '../primitives';
import { Button } from '../../components/ui/Button';
import { Checkbox, Field, Select, TextArea, TextInput } from '../../components/ui/Field';
import { api } from '../../lib/api';
import { getCaptchaToken, isCaptchaConfigured } from '../../lib/recaptcha';
import { useSite } from '../../lib/site-context';
import type { ContactFormSection } from '../../types/sections';

type Status = 'idle' | 'sending' | 'sent' | 'error';

interface Office { city: string; address: string; phone: string; email: string }

/**
 * Does this look like an e-mail address?
 *
 * The same check the API runs, and written the same way for the same reason: the usual
 * `/^[^\s@]+@[^\s@]+\.[^\s@]+$/` backtracks on an address with no dot after the `@`,
 * because `.` is inside `[^\s@]`. Here that is a slow keystroke rather than a way to load
 * the server, but a browser check that disagrees with the API's is worse than either.
 */
function looksLikeEmail(value: string): boolean {
  const email = value.trim();
  if (!email || /\s/.test(email)) return false;

  const at = email.indexOf('@');
  if (at < 1 || at !== email.lastIndexOf('@')) return false;

  const domain = email.slice(at + 1);
  const dot = domain.indexOf('.');
  return dot > 0 && dot < domain.length - 1 && !domain.endsWith('.');
}

export default function ContactForm({ data }: { data: ContactFormSection }) {
  const { region, locale, t } = useSite();
  const [departments, setDepartments] = useState<{ key: string; name: string }[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [status, setStatus] = useState<Status>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    api.contactDepartments(region.code, locale)
      .then((res) => { if (!cancelled) setDepartments(res); })
      .catch(() => { if (!cancelled) setDepartments([]); });
    if (data.showOffices) {
      api.offices(region.code)
        .then((res) => { if (!cancelled) setOffices(res); })
        .catch(() => { if (!cancelled) setOffices([]); });
    }
    return () => { cancelled = true; };
  }, [region.code, locale, data.showOffices]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // Held in a variable on purpose. `event.currentTarget` is only set while the event
    // is being dispatched, so after the `await` below it reads `null` - and
    // `event.currentTarget.reset()` then threw a TypeError *inside the try*, which the
    // catch turned into "the message could not be sent" on a submission the server had
    // already accepted with a 200.
    const formEl = event.currentTarget;
    const form = new FormData(formEl);

    const values = {
      contactPerson: String(form.get('contactPerson') ?? '').trim(),
      email: String(form.get('email') ?? '').trim(),
      phone: String(form.get('phone') ?? '').trim(),
      message: String(form.get('message') ?? '').trim(),
      departmentKey: String(form.get('departmentKey') ?? ''),
      consent: form.get('consent') === 'on',
      website: String(form.get('website') ?? '') // honeypot, must stay empty
    };

    const next: Record<string, string> = {};
    // The server's own minimums (createSubmission): a name of 2 and a message of 5. The form
    // used to ask only for "not empty", so a one-word reply like "ok" passed here and came
    // back as a 400 naming `message`.
    if (!values.contactPerson) next.contactPerson = t('form.required');
    else if (values.contactPerson.length < 2) next.contactPerson = t('form.tooShort');
    if (!values.email) next.email = t('form.required');
    else if (!looksLikeEmail(values.email)) next.email = t('form.invalidEmail');
    if (!values.message) next.message = t('form.required');
    else if (values.message.length < 5) next.message = t('form.tooShort');
    if (!values.departmentKey) next.departmentKey = t('form.required');
    if (!values.consent) next.consent = t('form.required');
    setErrors(next);
    if (Object.keys(next).length) return;

    setStatus('sending');
    // Only the request is inside the try. It used to wrap the success path too, so any
    // mistake after the call - the one above, or the next one - was reported to the
    // visitor as a failed send instead of showing up in the console.
    try {
      const captchaToken = isCaptchaConfigured() ? await getCaptchaToken('contact_form') : '';
      await api.submitContactForm({
        ...values,
        captchaToken,
        regionCode: region.code,
        localeCode: locale,
        consentVersion: 'v1',
        sourceUrl: typeof window !== 'undefined' ? window.location.href : undefined
      });
    } catch {
      setStatus('error');
      return;
    }

    setStatus('sent');
    formEl.reset();
  }

  return (
    <SectionShell settings={data.settings} name="contact-form">
      <div className="grid gap-10 lg:grid-cols-2">
        <div className="flex flex-col gap-4">
          {data.heading ? <Heading>{data.heading}</Heading> : null}
          {data.lead ? <Lead>{data.lead}</Lead> : null}

          {data.showOffices && offices.length ? (
            <div className="mt-4">
              <h3 className="mb-4 text-body-md font-semibold text-ink">{t('form.offices')}</h3>
              <ul className="grid gap-5 sm:grid-cols-2">
                {offices.map((office) => (
                  <li key={office.city}>
                    <p className="text-caption font-semibold uppercase tracking-widest text-brand-500">{office.city}</p>
                    <p className="mt-1 whitespace-pre-line text-body-sm text-ink-muted">{office.address}</p>
                    {office.phone ? <p className="text-body-sm text-ink-muted">{office.phone}</p> : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>

        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-6 rounded-m border border-grey-300 p-6">
          <Field id="departmentKey" label={t('form.department')} error={errors.departmentKey}>
            <Select id="departmentKey" name="departmentKey" defaultValue="" aria-invalid={Boolean(errors.departmentKey)}>
              <option value="" disabled>{t('form.departmentPlaceholder')}</option>
              {departments.map((d) => <option key={d.key} value={d.key}>{d.name}</option>)}
            </Select>
          </Field>

          <Field id="contactPerson" label={t('form.contactPerson')} required error={errors.contactPerson}>
            <TextInput id="contactPerson" name="contactPerson" type="text" autoComplete="name" aria-invalid={Boolean(errors.contactPerson)} />
          </Field>

          <div className="grid gap-6 sm:grid-cols-2">
            <Field id="email" label={t('form.email')} required error={errors.email}>
              <TextInput id="email" name="email" type="email" autoComplete="email" aria-invalid={Boolean(errors.email)} />
            </Field>
            <Field id="phone" label={t('form.phone')}>
              <TextInput id="phone" name="phone" type="tel" autoComplete="tel" />
            </Field>
          </div>

          <Field id="message" label={t('form.message')} required error={errors.message}>
            <TextArea id="message" name="message" rows={6} aria-invalid={Boolean(errors.message)} />
          </Field>

          {/* Honeypot: hidden from users, irresistible to bots. */}
          <div className="hidden" aria-hidden>
            <label htmlFor="website">Website</label>
            <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
          </div>

          <Checkbox
            id="consent"
            name="consent"
            error={errors.consent}
            label={<>{data.consentText
              ? <span className="weco-prose-inline"><Blocks value={data.consentText} /></span>
              : t('form.consent')} *</>}
          />

          <div>
            <Button type="submit" withIcon disabled={status === 'sending'}>
              {status === 'sending' ? t('form.sending') : t('form.submit')}
            </Button>
          </div>

          {status === 'sent' ? <p role="status" className="text-body-sm text-green-700">{t('form.success')}</p> : null}
          {status === 'error' ? <p role="alert" className="text-body-sm text-red-600">{t('form.error')}</p> : null}
        </form>
      </div>
    </SectionShell>
  );
}
